import { useEffect, useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import type { DisciplineVerzameling } from '@models/DisciplineVerzameling'
import type { Entity } from '@models/Entity'

import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import type { GeblokkeerdeWeek } from '../../../planning/types/GeblokkeerdeWeek'
import type { BijlageTemplate } from '../../../templates/types/BijlageTemplate'
import { PageSection } from '../../../theme/components/PageSection'
import { useOpenBijlageForEntity } from '../hooks/useOpenBijlageForEntity'
import type { AssignedBijlage } from '../types/AssignedBijlage'
import type { Bijlage } from '../types/Bijlage'
import type { BijlagenDialogType } from '../types/BijlagenDialogType'

import { AssignedBijlagen } from './AssignedBijlagen'
import { BijlagenDialog } from './BijlagenDialog'

interface Props {
  entity: Entity
  user: AuthenticatedUser | Record<string, any>
  assignees: any[]
  showCreateAssignedBijlage: boolean
  projectStatus: string
  projectStartUitvoeringWeek?: string
  isHoofdleidingProject?: boolean
  isVerantwoordelijke: boolean
  disciplineVerzamelingen: DisciplineVerzameling[]
  assignedBijlagen: AssignedBijlage[]
  bijlagen: Bijlage[]
  bijlageTemplates: BijlageTemplate[]
  geblokkeerdeWeken?: GeblokkeerdeWeek[]
}

export const AssignedBijlagenByProcesstap = ({
  entity,
  user,
  assignees,
  showCreateAssignedBijlage,
  projectStatus,
  projectStartUitvoeringWeek,
  isHoofdleidingProject = false,
  isVerantwoordelijke,
  disciplineVerzamelingen,
  assignedBijlagen,
  bijlagen,
  bijlageTemplates,
  geblokkeerdeWeken = [],
}: Props) => {
  const [type, setType] = useState<BijlagenDialogType>()
  const [selectedBijlage, setSelectedBijlage] = useState<
    Bijlage | AssignedBijlage | undefined
  >(undefined)
  const [selectedProcesstatus, setSelectedProcesstatus] = useState<string>('')

  const { mutate: openBijlageForEntity } = useOpenBijlageForEntity()

  const processtatussen = new Set(
    bijlageTemplates
      .filter((b) => b.scope === 'project')
      .map((b) => b.processtatus),
  )

  const defaultExpanded = !isHoofdleidingProject

  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setExpanded({
      [projectStatus]: true,
    })
  }, [projectStatus])

  const onOpen = (bijlage: Bijlage) => openBijlageForEntity({ entity, bijlage })

  const handleClick = ({
    type,
    bijlage,
  }: {
    type: BijlagenDialogType
    bijlage?: Bijlage | AssignedBijlage
  }) => {
    setSelectedBijlage(bijlage)
    setType(type)
  }

  const onCreate = (processtatus: string) => {
    setSelectedProcesstatus(processtatus)
    setType('create')
  }

  return (
    <PageSection heading="Bijlagen" subheading="Per processtap" id="bijlagen">
      <Box>
        {[...processtatussen].map((status) => {
          const assignedBijlagenForStatus = assignedBijlagen.filter(
            (assignedBijlage) => assignedBijlage.processtatus === status,
          )

          const bijlagenForAssignedBijlagen = bijlagen.filter((bijlage) =>
            assignedBijlagenForStatus.some(
              (assignedBijlage) => assignedBijlage._id === bijlage.parentId,
            ),
          )

          return (
            <Accordion
              key={status}
              expanded={
                expanded[status] !== undefined
                  ? expanded[status]
                  : defaultExpanded
              }
              onChange={(_, isExpanded) => {
                setExpanded({
                  ...expanded,
                  [status]: isExpanded,
                })
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{status}</Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ padding: 0 }}>
                <AssignedBijlagen
                  assignedBijlagen={assignedBijlagenForStatus}
                  bijlagen={bijlagenForAssignedBijlagen}
                  user={user}
                  onCreate={() => onCreate(status)}
                  onOpen={onOpen}
                  onUpload={(bijlage) =>
                    handleClick({ type: 'upload', bijlage })
                  }
                  onCancel={(bijlage) =>
                    handleClick({ type: 'cancel', bijlage })
                  }
                  onOpleveren={(bijlage) =>
                    handleClick({ type: 'submit', bijlage })
                  }
                  onReview={(bijlage) =>
                    handleClick({ type: 'review', bijlage })
                  }
                  onEdit={(bijlage) => handleClick({ type: 'edit', bijlage })}
                  showCreateAssignedBijlage={showCreateAssignedBijlage}
                  showCancelAssignedBijlage={
                    !isHoofdleidingProject || isVerantwoordelijke
                  }
                  isVerantwoordelijke={isVerantwoordelijke}
                />
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Box>

      <BijlagenDialog
        type={type}
        bijlage={selectedBijlage}
        assignees={assignees}
        onClose={() => setType(undefined)}
        disciplineVerzamelingen={disciplineVerzamelingen}
        bijlageTemplates={bijlageTemplates}
        entity={entity}
        projectStartUitvoeringWeek={projectStartUitvoeringWeek}
        processtatus={selectedProcesstatus}
        isHoofdleidingProject={isHoofdleidingProject}
        geblokkeerdeWeken={geblokkeerdeWeken}
      />
    </PageSection>
  )
}
